import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, WindowLocation } from '@gatsbyjs/reach-router';
import {
  AddressDeleteModal, AddressListView, AddressPanel, usePrefetchAddresses,
} from '@printdeal/address-frontend';
import { Heading, LinkAsButton, Button } from '@printdeal/ui-library';
import { navigate } from 'gatsby';
import { AddressDropdownItemProps } from '@printdeal/address-frontend/types';
import Breadcrumb from '../../../../Breadcrumb';
import Head from '../../../../PrivateHead';

const AddressListPage = () => {
  usePrefetchAddresses();

  const intl = useIntl();
  const location = useLocation() as WindowLocation<{
    showSuccessAlertForCreatedItem?: string;
    showSuccessAlertForUpdatedItem?: string;
  }>;

  const [showSuccessAlertForCreatedItem, setShowSuccessAlertForCreatedItem] = useState<string | undefined>(
    location.state?.showSuccessAlertForCreatedItem,
  );
  const [showSuccessAlertForUpdatedItem, setShowSuccessAlertForUpdatedItem] = useState<string | undefined>(
    location.state?.showSuccessAlertForUpdatedItem,
  );
  const [deleteAddressModalIsOpen, setDeleteAddressModalIsOpen] = useState<string | undefined>();

  return (
    <div className="pd-flex pd-flex-col pd-gap-3">
      <Head title={intl.formatMessage({ id: 'account.addressList.title', defaultMessage: 'My addressbook' })} />
      <Breadcrumb>
        <Breadcrumb.Item to="..">
          <FormattedMessage id="account.index.title" defaultMessage="My account" />
        </Breadcrumb.Item>
        <Breadcrumb.Item isCurrentPage>
          <FormattedMessage id="account.addressList.title" defaultMessage="My addressbook" />
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="pd-flex pd-items-center pd-justify-between">
        <Heading as="h2">
          <FormattedMessage id="account.addressList.title" defaultMessage="My addressbook" />
        </Heading>
        <Button iconBefore="plus" onClick={() => navigate('./create')}>
          <FormattedMessage id="address.add" defaultMessage="Add new address" />
        </Button>
      </div>

      <AddressListView
        showSuccessAlertForCreatedAddressId={showSuccessAlertForCreatedItem}
        showSuccessAlertForUpdatedAddressId={showSuccessAlertForUpdatedItem}
        renderAddressPanel={(data, focusedAddress) => {
          const dropdownItems: Array<AddressDropdownItemProps> = [];

          if (!data.isDefaultBillingAddress && !data.isDefaultShippingAddress) {
            dropdownItems.push({
              title: 'Delete address',
              iconBefore: 'trashcan',
              onClick: () => {
                setDeleteAddressModalIsOpen(data.address.id);
              },
            });
          }

          return (
            <>
              <AddressPanel
                key={data.address.id}
                address={data.address}
                isDefaultBillingAddress={data.isDefaultBillingAddress}
                isDefaultShippingAddress={data.isDefaultShippingAddress}
                className={focusedAddress === data.address.id ? 'pd-border-info-500' : undefined}
                dropdownItems={dropdownItems}
                footer={(
                  <Button
                    variant="secondary"
                    iconBefore="edit"
                    className="pd-w-full md:pd-w-auto"
                    onClick={() => navigate(`./edit/${data.address.id}`)}
                  >
                    <FormattedMessage id="address.button.editAddress" defaultMessage="Edit address" />
                  </Button>
                )}
              />

              <AddressDeleteModal
                addressId={data.address.id}
                isModalOpen={deleteAddressModalIsOpen === data.address.id}
                setIsModalOpen={setDeleteAddressModalIsOpen}
                onSuccess={() => {
                  setShowSuccessAlertForCreatedItem(undefined);
                  setShowSuccessAlertForUpdatedItem(undefined);
                }}
              />
            </>
          );
        }}
      />

      <LinkAsButton
        variant="secondary"
        iconBefore="chevron-left"
        className=" pd-self-start pd-text-sm"
        onClick={() => navigate('..')}
      >
        <FormattedMessage id="account.linkToAccount" defaultMessage="Link to account" />
      </LinkAsButton>
    </div>
  );
};

export default AddressListPage;
